import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
import { getIntersectionRoot, scrollContainerMediaQuery } from '@theme/scroll-container';
import { showToast } from '@theme/fifi-toast';
import { StandardEvents } from '@shopify/events';

/** Sent on the section when the design colour or the "Front photo" extra changes (fifi-product-gallery.js). */
const ORDER_CHANGE = 'fifi:order-change';
/** Sent on the section by the gallery's "See it in" dots. */
const DESIGN_COLOUR = 'fifi:design-colour';
/** The order-form version, saved on each line as `_order_form`. */
const FORM_VERSION = 'v1';

/**
 * @typedef {object} Choice
 * @property {string} label
 * @property {number} price - In pence.
 */

/**
 * @typedef {object} Extra
 * @property {string} key
 * @property {string} title
 * @property {boolean} included
 * @property {number} price - In pence; 0 when included.
 * @property {string} option - The option's key (the only option's for an extra without a choice).
 * @property {string} optionLabel - '' for an extra without a choice.
 * @property {string} colour - '' until picked.
 * @property {boolean} needsColour
 * @property {boolean} frontPhoto
 */

/**
 * What the customer chose, from the steps shown on this product (a step that isn't shown reads as
 * '' or null).
 * @typedef {object} OrderState
 * @property {string} name
 * @property {string} years
 * @property {Choice | null} shirt
 * @property {string} design - '' is "As pictured".
 * @property {{ label: string, elements: string[] } | null} style
 * @property {string} element1 - '' is "As pictured".
 * @property {string} element2
 * @property {string[]} elementNames - The shown element steps' names ('' when not shown).
 * @property {string} notes
 * @property {string} fit
 * @property {string} size
 * @property {Extra[]} extras - Chosen and included extras.
 */

/**
 * Translated templates from the section (`ref="strings"`); "__NAME__" marks a hole for `fill()`.
 * (Brace-free on purpose: a { or } inside a string in a Liquid tag breaks Shopify's parser.)
 * @typedef {object} Strings
 * @property {string} required
 * @property {string} optional
 * @property {string} included
 * @property {string} includedWithDesign
 * @property {string} asPictured
 * @property {string} added
 * @property {string} toGo
 * @property {string} ready
 * @property {string} extrasAdded
 * @property {string} photosAdded
 * @property {string} fitSize
 * @property {string} elementStep
 * @property {string} addPhoto
 * @property {string} photoSlot
 * @property {string} pickColour
 * @property {string} errorName
 * @property {string} errorYears
 * @property {string} errorStyle
 * @property {string} errorSize
 * @property {string} errorUnavailable
 * @property {string} errorPhotoSize
 * @property {string} nameYearsToAdd
 * @property {string} nameToAdd
 * @property {string} shirt
 * @property {string} shirtColour
 * @property {string} designAsPictured
 * @property {string} designColour
 * @property {string} sizeLine
 * @property {string} sizeOnly
 * @property {string} sizeToPick
 * @property {string} photosOne
 * @property {string} photosOther
 * @property {string} notes
 * @property {string} elementIn
 * @property {string} addToBag
 * @property {string} finish
 * @property {string} detailsOne
 * @property {string} detailsOther
 * @property {string} none
 * @property {string} propName
 * @property {string} propYears
 * @property {string} propShirt
 * @property {string} propDesign
 * @property {string} propStyle
 * @property {string} propElementColour
 * @property {string} propFit
 * @property {string} propSize
 * @property {string} propNotes
 * @property {string} propExtraPrice
 */

/**
 * @typedef {object} Refs
 * @property {HTMLScriptElement} variantMap - JSON: price offset in pence → variant id.
 * @property {HTMLScriptElement} strings - JSON: translated templates.
 * @property {HTMLElement} properties - Holds the hidden properties[…] inputs.
 * @property {HTMLElement} progressText
 * @property {HTMLElement} progressBar
 * @property {HTMLInputElement} [photoPicker]
 * @property {HTMLElement[]} [photoSlots]
 * @property {HTMLElement} cta - The receipt and Add to bag.
 * @property {HTMLElement} receiptLines
 * @property {HTMLTemplateElement} lineTemplate
 * @property {HTMLElement} total
 * @property {HTMLButtonElement} addButton
 * @property {HTMLElement} addTotal
 * @property {HTMLElement} bar - The sticky bar.
 * @property {HTMLElement} barTotal
 * @property {HTMLElement} barSub
 * @property {HTMLButtonElement} barButton
 */

/**
 * Fifi's V3 order form (design-handoff/design/Product V3.dc.html), rendered by
 * sections/fifi-product.liquid inside Horizon's product form.
 *
 * Reads the steps, keeps the step values, numbers, progress, receipt, totals and sticky bar up to
 * date, validates (name, years, style, a colour for each chosen trim, size), shows the "Check your
 * spelling" dialog, then picks the hidden price variant for base + extras, writes every answer as a
 * line-item property and submits the product form. Only the steps the section rendered for this
 * product (and, on a styled product, the chosen style's element steps) count anywhere. Horizon's
 * product-form-component does the add, refreshes the bag and fires the cart events; fifi-toast.js
 * shows the "Added to your bag" or error toast from those.
 *
 * @extends {Component<Refs>}
 */
class FifiOrderForm extends Component {
  requiredRefs = [
    'variantMap',
    'strings',
    'properties',
    'progressText',
    'progressBar',
    'cta',
    'receiptLines',
    'lineTemplate',
    'total',
    'addButton',
    'addTotal',
    'bar',
    'barTotal',
    'barSub',
    'barButton',
  ];

  /** @type {Strings} */
  #strings = /** @type {Strings} */ ({});

  /** @type {Map<number, string>} */
  #variants = new Map();

  /** Keys of the steps currently showing an error ('name', 'colour:glitter', …). @type {Set<string>} */
  #errors = new Set();

  /** @type {File[]} */
  #photos = [];

  /** @type {string[]} */
  #photoUrls = [];

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** custom.element_colour_limits, element name → colour names, both lower case. @type {Map<string, string[]> | null} */
  #colourLimits = null;

  #ctaOutOfView = false;
  #dialogOpen = false;
  #busy = false;
  #lastChange = '';

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.#strings = parseJson(this.refs.strings.textContent) ?? {};
    const map = parseJson(this.refs.variantMap.textContent) ?? {};
    for (const [offset, id] of Object.entries(map)) this.#variants.set(Number(offset), String(id));

    const { variantsCount, valuesCount } = this.refs.variantMap.dataset;
    if (Number(variantsCount) > this.#variants.size || Number(valuesCount) < Number(variantsCount)) {
      console.warn(
        `[fifi-order-form] ${this.#variants.size} of ${variantsCount} price variants found; extras totals without a variant can't be added.`
      );
    }

    const { signal } = this.#abortController;
    this.addEventListener('input', this.#onInput, { signal });
    this.addEventListener('change', this.#onChange, { signal });
    this.refs.photoPicker?.addEventListener('change', this.#onPhotosPicked, { signal });
    this.#section?.addEventListener(DESIGN_COLOUR, this.#onDesignColour, { signal });
    this.#productForm?.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate, { signal });
    this.#productForm?.addEventListener(StandardEvents.cartError, this.#onCartError, { signal });
    scrollContainerMediaQuery.addEventListener('change', this.#observeCta, { signal });

    for (const id of [this.dataset.confirmDialog, this.dataset.guideDialog]) {
      const dialog = id ? document.getElementById(id) : null;
      dialog?.addEventListener('dialog:open', this.#onDialogOpen, { signal });
      dialog?.addEventListener('dialog:close', this.#onDialogClose, { signal });
    }

    this.#observeCta();
    this.#update();

    // The gallery may connect after this form; send it the (possibly browser-restored) colour again.
    customElements.whenDefined('fifi-product-gallery').then(() => {
      this.#lastChange = '';
      this.#notifyGallery(this.#readState());
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#observer?.disconnect();
    for (const url of this.#photoUrls) URL.revokeObjectURL(url);
  }

  /* ---------------------------------------------------------------- actions (on:click) */

  /** "Add to bag": validate, then confirm or add. */
  add() {
    if (this.#busy) return;

    const state = this.#readState();
    const missing = this.#missing(state);
    if (missing.length) {
      this.#showErrors(missing);
      return;
    }

    const dialog = this.#confirmDialog;
    if (this.dataset.confirm === 'true' && dialog) {
      this.#fillConfirm(state);
      dialog.showDialog();
      return;
    }

    this.#commit(state);
  }

  /** "Looks perfect" in the confirm dialog. */
  confirm() {
    this.#confirmDialog?.closeDialog();
    if (this.#busy) return;

    const state = this.#readState();
    if (this.#missing(state).length) return;
    this.#commit(state);
  }

  /** Sticky bar button: jump to the first missing detail, or add. */
  barAction() {
    const missing = this.#missing(this.#readState());
    if (missing[0]) {
      this.#scrollToField(missing[0]);
    } else {
      this.add();
    }
  }

  /**
   * A years shortcut ("Secondary 2022–2027"): fills in the years part.
   * @param {Event} event
   */
  pickYears(event) {
    const button = /** @type {HTMLElement} */ (event.target);
    const label = button.dataset.yearChip ?? '';
    const match = label.match(/(\d{4})\s*[-–—]\s*(\d{2,4})/);
    const input = this.#field('years');
    if (!input) return;

    input.value = match ? `${match[1]}–${match[2]}` : label;
    this.#errors.delete('years');
    this.#update();
  }

  pickPhotos() {
    const picker = this.refs.photoPicker;
    if (!picker) return;
    picker.value = '';
    picker.click();
  }

  /** @param {number} index */
  removePhoto(index) {
    const position = Number(index);
    if (!Number.isInteger(position)) return;
    this.#photos.splice(position, 1);
    this.#renderPhotos();
    this.#update();
  }

  /* ---------------------------------------------------------------- events */

  /** @param {Event} event */
  #onInput = (event) => {
    const target = event.target;
    if (target instanceof HTMLInputElement && target.dataset.field === 'years') {
      const normalised = target.value.replace(/\s*[-–—]\s*/g, '–').replace(/^(\d{4})\s+(?=\d)/, '$1–');
      if (normalised !== target.value) target.value = normalised;
    }
    if (target instanceof HTMLElement && target.dataset.field) this.#errors.delete(target.dataset.field);
    this.#update();
  };

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    if (target.name === 'style') this.#errors.delete('style');
    if (target.name === 'size') this.#errors.delete('size');
    if (target.name === 'fit') this.#syncGuideFit(target);

    const extra = target.closest('[data-extra]');
    if (extra instanceof HTMLElement && extra.dataset.extra) {
      if (target.closest('[data-extra-colours]') || target.matches('[data-extra-toggle]')) {
        this.#errors.delete(`colour:${extra.dataset.extra}`);
      }
    }

    this.#update();
  };

  /** @param {Event} event */
  #onDesignColour = (event) => {
    const colour = /** @type {CustomEvent<{ colour: string }>} */ (event).detail?.colour ?? '';
    const radio = Array.from(this.#radios('design')).find((input) => input.value === colour);
    if (!radio || radio.checked) return;
    radio.checked = true;
    this.#update();
  };

  /** @param {Event} event */
  #onPhotosPicked = (event) => {
    const picker = /** @type {HTMLInputElement} */ (event.target);
    const maxBytes = Number(this.dataset.maxPhotoMb ?? 20) * 1024 * 1024;
    const files = Array.from(picker.files ?? []).filter((file) => file.type.startsWith('image/'));
    const fitting = files.filter((file) => file.size <= maxBytes);

    if (fitting.length < files.length) showToast({ tone: 'error', message: this.#strings.errorPhotoSize });

    this.#photos = [...this.#photos, ...fitting].slice(0, 3);
    this.#renderPhotos();
    this.#update();
  };

  /** Keeps the button disabled until Horizon's add settles. @param {Event} event */
  #onCartLinesUpdate = (event) => {
    if (event.target !== this.#productForm) return;
    const promise = /** @type {{ promise?: Promise<unknown> }} */ (event).promise;
    if (!promise) {
      this.#setBusy(false);
      return;
    }
    promise.catch(() => {}).finally(() => this.#setBusy(false));
  };

  #onCartError = () => {
    this.#setBusy(false);
  };

  #onDialogOpen = () => {
    this.#dialogOpen = true;
    this.#renderBarVisibility();
  };

  #onDialogClose = () => {
    this.#dialogOpen = false;
    this.#renderBarVisibility();
  };

  /* ---------------------------------------------------------------- state */

  /**
   * Steps the product left out aren't rendered, so their fields read as ''. Element steps can be
   * rendered but hidden (a styled product before a style is chosen), so they go by `hidden`.
   * @returns {OrderState}
   */
  #readState() {
    const shirt = this.#checked('shirt');
    const style = this.#checked('style');
    const styleElements = style?.dataset.elements?.split('|').filter(Boolean) ?? [];

    const elementNames = [1, 2].map((number, index) => {
      const step = this.querySelector(`[data-step="element${number}"]`);
      if (!(step instanceof HTMLElement) || step.hidden) return '';
      return step.hasAttribute('data-style-element') ? styleElements[index] ?? '' : step.dataset.elementName ?? '';
    });

    return {
      name: (this.#field('name')?.value ?? '').trim(),
      years: (this.#field('years')?.value ?? '').trim(),
      shirt: shirt ? { label: shirt.dataset.label ?? shirt.value, price: Number(shirt.dataset.price) || 0 } : null,
      design: this.#checked('design')?.value ?? '',
      style: style ? { label: style.dataset.label ?? style.value, elements: styleElements } : null,
      element1: elementNames[0] ? this.#checked('element1')?.value ?? '' : '',
      element2: elementNames[1] ? this.#checked('element2')?.value ?? '' : '',
      elementNames,
      notes: (this.#field('notes')?.value ?? '').trim(),
      fit: this.#checked('fit')?.dataset.label ?? '',
      size: this.#checked('size')?.dataset.label ?? '',
      extras: this.#readExtras(),
    };
  }

  /** @returns {Extra[]} */
  #readExtras() {
    /** @type {Extra[]} */
    const extras = [];

    for (const element of this.querySelectorAll('[data-extra]')) {
      if (!(element instanceof HTMLElement)) continue;
      const toggle = element.querySelector('[data-extra-toggle]');
      const included = element.hasAttribute('data-included');
      if (!included && !(toggle instanceof HTMLInputElement && toggle.checked)) continue;

      const option = element.querySelector('input[name$="-option"]:checked');
      const colour = element.querySelector('[data-extra-colours] input:checked');
      const optionInput = option instanceof HTMLInputElement ? option : null;
      const price = included ? 0 : Number(optionInput?.dataset.price ?? element.dataset.price) || 0;

      extras.push({
        key: element.dataset.extra ?? '',
        title: element.dataset.title ?? '',
        included,
        price,
        option: optionInput?.value ?? element.dataset.option ?? '',
        optionLabel: optionInput?.dataset.label ?? '',
        colour: colour instanceof HTMLInputElement ? colour.value : '',
        needsColour: !included && Boolean(element.querySelector('[data-extra-colours]')),
        frontPhoto: element.hasAttribute('data-front-photo'),
      });
    }

    return extras;
  }

  /**
   * What's still needed, in page order.
   * @param {OrderState} state
   * @returns {string[]}
   */
  #missing(state) {
    const missing = [];
    if (!state.name) missing.push('name');
    if (this.#hasStep('years') && !/\d/.test(state.years)) missing.push('years');
    if (this.#hasStep('style') && !state.style) missing.push('style');
    for (const extra of state.extras) {
      if (extra.needsColour && !extra.colour) missing.push(`colour:${extra.key}`);
    }
    if (this.#hasSizes && !state.size) missing.push('size');
    return missing;
  }

  /**
   * Shirt colour + chosen extras, in pence.
   * @param {OrderState} state
   */
  #offset(state) {
    return (state.shirt?.price ?? 0) + state.extras.reduce((sum, extra) => sum + extra.price, 0);
  }

  /* ---------------------------------------------------------------- render */

  #update() {
    this.#syncElementSteps();
    this.#renumber();

    const state = this.#readState();
    const missing = this.#missing(state);
    const strings = this.#strings;
    const offset = this.#offset(state);
    const total = this.#money(this.#basePrice + offset);
    const chosen = state.extras.filter((extra) => !extra.included);
    const yearsOk = /\d/.test(state.years);

    // Step headers
    this.#setStep('name', Boolean(state.name), state.name || strings.required);
    this.#setStep('years', yearsOk, yearsOk ? state.years : strings.required);
    this.#setStep('shirt', true, state.shirt?.label ?? '');
    this.#setStep('design', true, state.design || strings.asPictured);
    this.#setStep('style', Boolean(state.style), state.style?.label ?? strings.required);
    this.#setStep('element1', true, state.element1 || strings.asPictured);
    this.#setStep('element2', true, state.element2 || strings.asPictured);
    this.#setStep(
      'photos',
      this.#photos.length > 0,
      this.#photos.length ? fill(strings.photosAdded, { count: this.#photos.length }) : strings.optional
    );
    this.#setStep('notes', Boolean(state.notes), state.notes ? strings.added : strings.optional);
    this.#setStep(
      'extras',
      chosen.length > 0,
      chosen.length ? fill(strings.extrasAdded, { count: chosen.length, amount: this.#money(offset) }) : strings.optional
    );
    this.#setStep('size', Boolean(state.size), state.size ? this.#fitSize(state) : strings.required);

    // Progress: name, then whichever of years, style, size and trim colours this product asks for
    const required =
      1 +
      Number(this.#hasStep('years')) +
      Number(this.#hasStep('style')) +
      Number(this.#hasSizes) +
      chosen.filter((extra) => extra.needsColour).length;
    this.refs.progressText.textContent = missing.length ? fill(strings.toGo, { count: missing.length }) : strings.ready;
    this.refs.progressText.toggleAttribute('data-ready', missing.length === 0);
    this.refs.progressBar.style.width = `${Math.round(((required - missing.length) / Math.max(required, 1)) * 100)}%`;

    this.#renderFields(state);
    this.#renderExtras(state);
    this.#renderErrors();
    this.#renderReceipt(state);

    this.refs.total.textContent = total;
    this.refs.addTotal.textContent = total;
    this.refs.barTotal.textContent = total;
    this.refs.barSub.textContent = missing.length
      ? fill(missing.length === 1 ? strings.detailsOne : strings.detailsOther, {
          count: missing.length,
          title: this.dataset.title ?? '',
        })
      : this.#summary(state);
    this.refs.barButton.textContent = missing.length ? strings.finish : strings.addToBag;

    this.#notifyGallery(state);
  }

  /**
   * @param {string} key
   * @param {boolean} done
   * @param {string} value
   */
  #setStep(key, done, value) {
    const step = this.querySelector(`[data-step="${key}"]`);
    if (!(step instanceof HTMLElement)) return;
    step.toggleAttribute('data-done', done);
    const valueElement = step.querySelector('[data-value]');
    if (valueElement) valueElement.textContent = value;
  }

  /**
   * On a styled product, shows one element step per element of the chosen style (none before),
   * headed "Colour of [element]" like the element-name steps the section renders.
   */
  #syncElementSteps() {
    const elements = this.#checked('style')?.dataset.elements?.split('|').filter(Boolean) ?? [];
    for (const step of this.querySelectorAll('[data-style-element]')) {
      if (!(step instanceof HTMLElement)) continue;
      const element = elements[Number(step.dataset.step?.replace('element', '')) - 1];
      step.hidden = !element;
      if (!element) continue;

      const heading = fill(this.#strings.elementStep, { element });
      const label = step.querySelector('.fifi-order__label');
      if (label && label.textContent !== heading) label.textContent = heading;
      step.querySelector('[role="radiogroup"]')?.setAttribute('aria-label', heading);
      this.#limitColours(step, element);
    }
  }

  /**
   * Shows only the element's colours from custom.element_colour_limits, in that order ("As pictured"
   * first), or every colour in the editor's order when it has no entry. A chosen colour that's no
   * longer offered goes back to "As pictured". (The section does this in Liquid for element names.)
   * @param {HTMLElement} step
   * @param {string} element
   */
  #limitColours(step, element) {
    const row = step.querySelector('.fifi-swatches');
    if (!(row instanceof HTMLElement)) return;

    const swatches = /** @type {HTMLElement[]} */ (
      Array.from(row.children).filter((child) => child instanceof HTMLElement)
    );
    swatches.forEach((swatch, index) => {
      if (swatch.dataset.order === undefined) swatch.dataset.order = String(index);
    });
    swatches.sort((a, b) => Number(a.dataset.order) - Number(b.dataset.order));

    /** @param {HTMLElement} swatch */
    const colourOf = (swatch) => (swatch.querySelector('input')?.value ?? '').trim().toLowerCase();
    const wanted = this.#limitsFor(element);
    const shown = wanted
      ? [
          ...swatches.filter((swatch) => colourOf(swatch) === ''),
          ...wanted.flatMap((colour) => swatches.find((swatch) => colourOf(swatch) === colour) ?? []),
        ]
      : swatches;
    const order = [...new Set(shown), ...swatches.filter((swatch) => !shown.includes(swatch))];

    if (order.some((swatch, index) => row.children[index] !== swatch)) row.append(...order);
    for (const swatch of swatches) swatch.hidden = !shown.includes(swatch);

    const checked = row.querySelector('input:checked');
    if (checked?.closest('[hidden]')) {
      const asPictured = row.querySelector('input[value=""]');
      if (asPictured instanceof HTMLInputElement) asPictured.checked = true;
    }
  }

  /**
   * @param {string} element
   * @returns {string[] | undefined}
   */
  #limitsFor(element) {
    if (!this.#colourLimits) {
      this.#colourLimits = new Map();
      const entries = parseJson(this.dataset.colourLimits ?? '');
      for (const entry of Array.isArray(entries) ? entries : []) {
        const text = String(entry);
        const colon = text.indexOf(':');
        if (colon < 0) continue;
        const colours = text
          .slice(colon + 1)
          .split(',')
          .map((colour) => colour.trim().toLowerCase())
          .filter(Boolean);
        this.#colourLimits.set(text.slice(0, colon).trim().toLowerCase(), colours);
      }
    }
    return this.#colourLimits.get(element.trim().toLowerCase());
  }

  /** Numbers the shown steps 1, 2, 3… */
  #renumber() {
    let number = 0;
    for (const step of this.querySelectorAll('[data-step]')) {
      if (!(step instanceof HTMLElement) || step.hidden) continue;
      number += 1;
      const circle = step.querySelector('.fifi-order__num');
      if (circle) circle.textContent = String(number);
    }
  }

  /** Counters, year shortcut states. @param {OrderState} state */
  #renderFields(state) {
    for (const counter of this.querySelectorAll('[data-count]')) {
      if (!(counter instanceof HTMLElement)) continue;
      const field = this.#field(counter.dataset.count ?? '');
      if (!field) continue;
      const max = field.maxLength;
      counter.textContent = `${field.value.length}/${max}`;
      counter.toggleAttribute('data-full', field.value.length >= max);
    }

    for (const chip of this.querySelectorAll('[data-year-chip]')) {
      if (!(chip instanceof HTMLElement)) continue;
      const match = (chip.dataset.yearChip ?? '').match(/(\d{4})\s*[-–—]\s*(\d{2,4})/);
      const selected = Boolean(match && state.years === `${match[1]}–${match[2]}`);
      chip.classList.toggle('fifi-tag--selected', selected);
      chip.setAttribute('aria-pressed', String(selected));
    }
  }

  /** Open panels, prices and colour names. @param {OrderState} state */
  #renderExtras(state) {
    for (const element of this.querySelectorAll('[data-extra]')) {
      if (!(element instanceof HTMLElement) || element.hasAttribute('data-included')) continue;

      const key = element.dataset.extra ?? '';
      const extra = state.extras.find((item) => item.key === key);
      const selected = Boolean(extra);
      element.toggleAttribute('data-selected', selected);

      const panel = element.querySelector('[data-extra-panel]');
      if (panel instanceof HTMLElement) panel.hidden = !selected;
      element.toggleAttribute('data-open', selected && panel instanceof HTMLElement);

      const from = element.querySelector('[data-extra-from]');
      if (from instanceof HTMLElement) from.hidden = selected;

      const price = `+${this.#money(extra ? extra.price : Number(element.dataset.price) || 0)}`;
      const priceElement = element.querySelector('[data-extra-price]');
      if (priceElement) priceElement.textContent = price;
      const priceCopy = element.querySelector('[data-extra-price-copy]');
      if (priceCopy) priceCopy.textContent = price;

      const colourValue = element.querySelector('[data-colour-value]');
      if (colourValue) colourValue.textContent = extra?.colour || this.#strings.pickColour;
    }
  }

  #renderErrors() {
    const messages = /** @type {Record<string, string | undefined>} */ ({
      name: this.#strings.errorName,
      years: this.#strings.errorYears,
      style: this.#strings.errorStyle,
      size: this.#strings.errorSize,
    });

    for (const key of ['name', 'years', 'style', 'size']) {
      const on = this.#errors.has(key);
      const error = this.querySelector(`[data-error="${key}"]`);
      if (error instanceof HTMLElement) {
        error.hidden = !on;
        error.textContent = on ? messages[key] ?? '' : '';
      }
      const step = this.querySelector(`[data-step="${key}"]`);
      step?.toggleAttribute('data-error', on);
      const field = this.#field(key);
      if (field) field.setAttribute('aria-invalid', String(on));
    }

    for (const element of this.querySelectorAll('[data-extra]')) {
      if (!(element instanceof HTMLElement)) continue;
      element.toggleAttribute('data-colour-error', this.#errors.has(`colour:${element.dataset.extra}`));
    }
  }

  /** @param {OrderState} state */
  #renderReceipt(state) {
    const strings = this.#strings;
    const lines = [];
    const quotedName = state.name ? `“${state.name}”` : '';
    const yearsOk = /\d/.test(state.years);

    lines.push({
      label: this.dataset.title ?? '',
      amount: this.#money(this.#basePrice),
      sub:
        [quotedName, yearsOk ? state.years : ''].filter(Boolean).join(' · ') ||
        (this.#hasStep('years') ? strings.nameYearsToAdd : strings.nameToAdd),
      main: true,
    });

    const shirtPrice = state.shirt?.price ?? 0;
    lines.push({
      label: this.#shirtLine(state) || strings.shirt,
      amount: shirtPrice ? `+${this.#money(shirtPrice)}` : strings.included,
      sub: [
        state.style?.label ?? '',
        this.#elementLine(state),
        state.size ? this.#sizeLine(state) : this.#hasSizes ? strings.sizeToPick : '',
      ]
        .filter(Boolean)
        .join(' · '),
    });

    const photosText = this.#photosText();
    if (photosText || state.notes) {
      lines.push({
        label: [photosText, state.notes ? strings.notes : ''].filter(Boolean).join(' · '),
        amount: strings.included,
        sub: '',
      });
    }

    for (const extra of state.extras) {
      if (extra.included) continue;
      lines.push({ label: this.#extraDetail(extra), amount: `+${this.#money(extra.price)}`, sub: '' });
    }

    const fragment = document.createDocumentFragment();
    for (const line of lines) {
      const node = /** @type {DocumentFragment} */ (this.refs.lineTemplate.content.cloneNode(true));
      const label = node.querySelector('.fifi-receipt__label');
      const amount = node.querySelector('.fifi-receipt__amount');
      const sub = node.querySelector('.fifi-receipt__sub');
      if (label) {
        label.textContent = line.label;
        label.classList.toggle('fifi-receipt__label--main', Boolean(line.main));
      }
      if (amount) amount.textContent = line.amount;
      if (sub) {
        if (line.sub) sub.textContent = line.sub;
        else sub.remove();
      }
      fragment.append(node);
    }
    this.refs.receiptLines.replaceChildren(fragment);
  }

  #renderPhotos() {
    for (const url of this.#photoUrls) URL.revokeObjectURL(url);
    this.#photoUrls = this.#photos.map((file) => URL.createObjectURL(file));

    const slots = this.refs.photoSlots ?? [];
    slots.forEach((slot, index) => {
      const url = this.#photoUrls[index];
      const preview = slot.querySelector('[data-photo-preview]');
      const add = slot.querySelector('[data-photo-add]');
      const image = slot.querySelector('[data-photo-image]');
      const label = slot.querySelector('[data-photo-add-label]');

      if (preview instanceof HTMLElement) preview.hidden = !url;
      if (add instanceof HTMLElement) add.hidden = Boolean(url);
      if (image instanceof HTMLImageElement) image.src = url ?? 'data:,';
      if (label) {
        label.textContent =
          index === this.#photos.length
            ? this.#strings.addPhoto
            : fill(this.#strings.photoSlot, { number: index + 1 });
      }
    });
  }

  #renderBarVisibility() {
    const visible = this.#ctaOutOfView && !this.#dialogOpen;
    this.refs.bar.dataset.visible = String(visible);
    this.refs.bar.toggleAttribute('inert', !visible);
  }

  /** Tells the gallery which colour set and whether to show the Front photo. @param {OrderState} state */
  #notifyGallery(state) {
    const pocket = state.extras.some((extra) => extra.frontPhoto);
    const change = `${state.design}|${pocket}`;
    if (change === this.#lastChange) return;
    this.#lastChange = change;
    this.#section?.dispatchEvent(new CustomEvent(ORDER_CHANGE, { detail: { colour: state.design, pocket } }));
  }

  /* ---------------------------------------------------------------- validation */

  /** @param {string[]} missing */
  #showErrors(missing) {
    this.#errors = new Set(missing);
    this.#update();
    if (missing[0]) this.#scrollToField(missing[0]);
  }

  /** @param {string} key */
  #scrollToField(key) {
    const target = key.startsWith('colour:')
      ? this.querySelector(`[data-extra="${CSS.escape(key.slice(7))}"] [data-extra-colours]`)?.parentElement
      : this.querySelector(`[data-step="${key}"]`);
    if (!(target instanceof HTMLElement)) return;

    target.scrollIntoView({ behavior: 'smooth', block: 'start' });

    if (key === 'name' || key === 'years') {
      const field = this.#field(key);
      window.setTimeout(() => field?.focus({ preventScroll: true }), 450);
    }
  }

  /* ---------------------------------------------------------------- add */

  /** @param {OrderState} state */
  #commit(state) {
    const offset = this.#offset(state);
    const maxExtras = Number(this.dataset.maxExtras ?? 15000);
    const variantId = offset <= maxExtras ? this.#variants.get(offset) : undefined;
    const form = this.closest('form');
    const idInput = form?.querySelector('input[name="id"]');

    if (!variantId || !form || !(idInput instanceof HTMLInputElement)) {
      showToast({ tone: 'error', message: this.#strings.errorUnavailable });
      return;
    }

    idInput.value = variantId;
    this.#writeProperties(state, offset);
    this.#syncPhotoInputs();
    this.#setBusy(true);
    form.requestSubmit();
  }

  /**
   * Every answer as a line-item property, in order (the bag and the toast show the first ones).
   * Keys starting with "_" are hidden from customers and used by the Flow price check.
   * @param {OrderState} state
   * @param {number} offset
   */
  #writeProperties(state, offset) {
    const strings = this.#strings;
    /** @type {[string, string][]} */
    const entries = [
      [strings.propName, state.name],
      [strings.propYears, state.years],
      [strings.propShirt, state.shirt?.label ?? ''],
    ];

    if (this.#hasStep('design')) entries.push([strings.propDesign, state.design || strings.asPictured]);
    if (state.style) entries.push([strings.propStyle, state.style.label]);

    [state.element1, state.element2].forEach((colour, index) => {
      const element = state.elementNames[index];
      if (colour && element) entries.push([fill(strings.propElementColour, { element }), colour]);
    });

    for (const extra of state.extras) {
      const detail = [extra.optionLabel, extra.colour].filter(Boolean).join(' · ');
      const price = this.#money(extra.price);
      let value = detail ? fill(strings.propExtraPrice, { detail, price }) : `+${price}`;
      if (extra.included) value = strings.includedWithDesign;
      entries.push([extra.title, value]);
    }

    if (state.fit) entries.push([strings.propFit, state.fit]);
    if (state.size) entries.push([strings.propSize, state.size]);
    if (state.notes) entries.push([strings.propNotes, state.notes]);

    const machineExtras = state.extras.map((extra) => `${extra.key}|${extra.included ? 'included' : extra.option}|${pounds(extra.price)}`);
    if (state.shirt?.price) machineExtras.unshift(`shirt|${state.shirt.label.toLowerCase()}|${pounds(state.shirt.price)}`);

    entries.push(
      ['_base_price', pounds(this.#basePrice)],
      ['_extras_total', pounds(offset)],
      ['_extras', machineExtras.join(';')],
      ['_order_form', FORM_VERSION]
    );

    const inputs = entries
      .filter(([, value]) => value !== '')
      .map(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[${key}]`;
        input.value = value;
        return input;
      });
    this.refs.properties.replaceChildren(...inputs);
  }

  /** Copies the chosen photos into the named file inputs; empty ones are disabled so they aren't sent. */
  #syncPhotoInputs() {
    (this.refs.photoSlots ?? []).forEach((slot, index) => {
      const input = slot.querySelector('[data-photo-input]');
      if (!(input instanceof HTMLInputElement)) return;
      const file = this.#photos[index];
      const transfer = new DataTransfer();
      if (file) transfer.items.add(file);
      input.files = transfer.files;
      input.disabled = !file;
    });
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    this.#busy = busy;
    this.refs.addButton.disabled = busy;
    this.refs.addButton.setAttribute('aria-busy', String(busy));
    this.refs.barButton.disabled = busy;
  }

  /** @param {OrderState} state */
  #fillConfirm(state) {
    const dialog = this.dataset.confirmDialog ? document.getElementById(this.dataset.confirmDialog) : null;
    if (!dialog) return;
    const strings = this.#strings;
    const extras = state.extras.filter((extra) => !extra.included).map((extra) => this.#extraDetail(extra));

    /** @type {Record<string, string>} */
    const rows = {
      name: state.name || '—',
      years: state.years || '—',
      shirt: state.shirt?.label ?? '—',
      design: state.design || strings.asPictured || '',
      style: state.style?.label ?? '—',
      elements: this.#elementLine(state) || strings.asPictured || '',
      extras: extras.length ? extras.join(', ') : strings.none,
      size: [state.fit, state.size || '—'].filter(Boolean).join(' · '),
    };

    /** Rows for the steps this product shows. @type {Record<string, boolean>} */
    const shown = {
      name: true,
      years: this.#hasStep('years'),
      shirt: this.#hasStep('shirt'),
      design: this.#hasStep('design'),
      style: this.#hasStep('style'),
      elements: state.elementNames.some(Boolean),
      extras: this.#hasStep('extras'),
      size: true,
    };

    for (const [key, value] of Object.entries(rows)) {
      const cell = dialog.querySelector(`[data-confirm="${key}"]`);
      if (cell) cell.textContent = value;
      const row = dialog.querySelector(`[data-confirm-row="${key}"]`);
      if (row instanceof HTMLElement) row.hidden = !shown[key];
    }

    const line = dialog.querySelector('[data-confirm-line]');
    if (line) line.textContent = `‘${[state.name, state.years].filter(Boolean).join(' · ')}’`;
    const total = dialog.querySelector('[data-confirm-total]');
    if (total) total.textContent = this.#money(this.#basePrice + this.#offset(state));
  }

  /* ---------------------------------------------------------------- sticky bar */

  /** (Re)observes the receipt; the scroll container changes across 990px. */
  #observeCta = () => {
    this.#observer?.disconnect();
    const desktop = scrollContainerMediaQuery.matches;

    this.#observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        const rootTop = entry.rootBounds?.top ?? 0;
        // Desktop: once the receipt has scrolled up past the header. Phone: whenever it's out of view.
        this.#ctaOutOfView = desktop
          ? !entry.isIntersecting && entry.boundingClientRect.top < rootTop
          : !entry.isIntersecting;
        this.#renderBarVisibility();
      },
      { root: getIntersectionRoot(), rootMargin: desktop ? '-80px 0px 0px 0px' : '-60px 0px -40px 0px' }
    );
    this.#observer.observe(this.refs.cta);
  };

  /* ---------------------------------------------------------------- helpers */

  get #basePrice() {
    return Number(this.dataset.basePrice) || 0;
  }

  get #hasSizes() {
    return Boolean(this.querySelector('input[name="size"]'));
  }

  get #section() {
    return this.closest('.shopify-section');
  }

  get #productForm() {
    return this.closest('product-form-component');
  }

  /** @returns {import('@theme/dialog').DialogComponent | null} */
  get #confirmDialog() {
    const id = this.dataset.confirmDialog;
    const dialog = id ? document.getElementById(id) : null;
    return dialog && 'showDialog' in dialog ? /** @type {import('@theme/dialog').DialogComponent} */ (dialog) : null;
  }

  /** @param {string} key */
  #hasStep(key) {
    return Boolean(this.querySelector(`[data-step="${key}"]`));
  }

  /**
   * @param {string} key
   * @returns {HTMLInputElement | HTMLTextAreaElement | null}
   */
  #field(key) {
    const field = this.querySelector(`[data-field="${key}"]`);
    return field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement ? field : null;
  }

  /** @param {string} name */
  #radios(name) {
    return /** @type {NodeListOf<HTMLInputElement>} */ (this.querySelectorAll(`input[type="radio"][name="${name}"]`));
  }

  /** @param {string} name */
  #checked(name) {
    const input = this.querySelector(`input[type="radio"][name="${name}"]:checked`);
    return input instanceof HTMLInputElement ? input : null;
  }

  /** @param {number} cents */
  #money(cents) {
    return formatMoney(cents, this.dataset.moneyFormat || '£{{amount}}', this.dataset.currency || 'GBP');
  }

  /** @param {OrderState} state */
  #designLabel(state) {
    return state.design
      ? fill(this.#strings.designColour, { colour: state.design.toLowerCase() })
      : this.#strings.designAsPictured;
  }

  /** "White shirt · design as pictured", from the shirt and design steps this product shows. @param {OrderState} state */
  #shirtLine(state) {
    return [
      state.shirt ? fill(this.#strings.shirtColour, { shirt: state.shirt.label }) : '',
      this.#hasStep('design') ? this.#designLabel(state) : '',
    ]
      .filter(Boolean)
      .join(' · ');
  }

  /** "Female fit · 14–15", or just the size without the fit step. @param {OrderState} state */
  #fitSize(state) {
    return state.fit ? fill(this.#strings.fitSize, { fit: state.fit, size: state.size }) : state.size;
  }

  /** "Female fit · size 14–15", or "Size 14–15" without the fit step. @param {OrderState} state */
  #sizeLine(state) {
    return state.fit
      ? fill(this.#strings.sizeLine, { fit: state.fit, size: state.size })
      : fill(this.#strings.sizeOnly, { size: state.size });
  }

  /** @param {OrderState} state */
  #elementLine(state) {
    return [state.element1, state.element2]
      .map((colour, index) => {
        const element = state.elementNames[index];
        if (!colour || !element) return '';
        return fill(this.#strings.elementIn, { element, colour: colour.toLowerCase() });
      })
      .filter(Boolean)
      .join(', ');
  }

  /** "Ribbon bows · arms & bottom · baby pink" @param {Extra} extra */
  #extraDetail(extra) {
    return [extra.title, extra.optionLabel.toLowerCase(), extra.colour.toLowerCase()].filter(Boolean).join(' · ');
  }

  #photosText() {
    const count = this.#photos.length;
    if (!count) return '';
    return fill(count === 1 ? this.#strings.photosOne : this.#strings.photosOther, { count });
  }

  /** The sticky bar's summary once everything is filled in. @param {OrderState} state */
  #summary(state) {
    return [
      `“${state.name}”`,
      state.years,
      this.#shirtLine(state),
      state.style?.label ?? '',
      this.#elementLine(state),
      state.size ? this.#fitSize(state) : '',
      ...state.extras.filter((extra) => !extra.included).map((extra) => this.#extraDetail(extra)),
    ]
      .filter(Boolean)
      .join(' · ');
  }

  /** Shows the size guide for the chosen fit. @param {HTMLInputElement} fit */
  #syncGuideFit(fit) {
    const dialog = this.dataset.guideDialog ? document.getElementById(this.dataset.guideDialog) : null;
    const radio = dialog?.querySelector(`input[data-guide-fit][value="${fit.dataset.guide ?? 'female'}"]`);
    if (radio instanceof HTMLInputElement) radio.checked = true;
  }
}

/**
 * Fills "__NAME__" holes in a translated template from `values.name`.
 * @param {string | undefined} template
 * @param {Record<string, string | number>} values
 */
function fill(template, values) {
  return (template ?? '').replace(/__([A-Z]+)__/g, (hole, key) => {
    const name = key.toLowerCase();
    return name in values ? String(values[name]) : hole;
  });
}

/** @param {number} cents */
function pounds(cents) {
  return (cents / 100).toFixed(2);
}

/** @param {string | null} text */
function parseJson(text) {
  try {
    return JSON.parse(text ?? '');
  } catch {
    return null;
  }
}

if (!customElements.get('fifi-order-form')) customElements.define('fifi-order-form', FifiOrderForm);
